import React, { useState } from 'react'
import { User } from 'api/typings'
import { Box, Button, Flex, Loading, Message, Modal, Text } from '@workwhile/ui'
import { useUpdateDobMutation, usePersonaIdPhoto } from 'queries/user'

export function DateOfBirth({
  id,
  dob,
  identityVerificationId,
  refreshData,
}: Pick<User, 'id' | 'dob' | 'identityVerificationId'> & {
  refreshData: () => void
}) {
  const [newDob, setNewDob] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [photoEnabled, setPhotoEnabled] = useState(false)
  const {
    mutate: submitDob,
    error,
    isPending,
    isSuccess,
  } = useUpdateDobMutation(id ?? '', {
    onSuccess: () => {
      setNewDob('')
      refreshData()
    },
  })
  const {
    data: imageUrl,
    isLoading: imageLoading,
    isError: imageError,
    refetch: refetchPhoto,
  } = usePersonaIdPhoto(id, { enabled: photoEnabled })

  return (
    <>
      <Box backgroundColor="offWhite" p={2} mb={2}>
        <Text fontWeight="bold">Current Date of Birth:</Text>
        <Text>{dob ?? 'Not set'}</Text>
      </Box>
      <Flex>
        {identityVerificationId ? (
          <Box
            width={200}
            minHeight={130}
            backgroundColor="offWhite"
            style={{ flexShrink: 0 }}
          >
            <Message variant="warning" mb={2}>
              <Text fontSize={2} fontWeight="bold">
                Sensitive Information
              </Text>
              <Text fontSize={0}>
                This document contains PII. Handle with care and do not share.
              </Text>
            </Message>
            {imageLoading ? (
              <Loading />
            ) : imageUrl ? (
              <>
                <img
                  src={imageUrl}
                  alt="Persona Government ID"
                  onClick={() => setIsModalOpen(true)}
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block',
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                />
                <Text
                  fontSize={0}
                  textAlign="center"
                  mt={1}
                  color="textSecondary"
                >
                  Government ID from Persona
                  <br />
                  (click to expand)
                </Text>
              </>
            ) : (
              <Button
                variant="secondary"
                onClick={() => {
                  if (imageError) {
                    refetchPhoto()
                  } else {
                    setPhotoEnabled(true)
                  }
                }}
                disabled={imageLoading}
              >
                {imageError ? 'Retry Load ID Photo' : 'Load ID Photo'}
              </Button>
            )}
          </Box>
        ) : null}
        <Box
          flex={1}
          ml={identityVerificationId ? 2 : 0}
          p={3}
          backgroundColor="offWhite"
        >
          <Text fontWeight="bold" mb={2}>
            Update Date of Birth
          </Text>
          <Text fontSize={1} mb={2}>
            Use this when a worker's entered DOB does not match their ID, and
            you have verified the correct date.
          </Text>
          <input
            disabled={isPending}
            type="date"
            value={newDob}
            onChange={(e) => setNewDob(e.target.value)}
            style={{ padding: '6px 8px', fontSize: 14 }}
          />
          {error ? (
            <Message mt={3} variant="error">
              {error.message}
            </Message>
          ) : null}
          {isSuccess ? (
            <Message mt={3} variant="success">
              Date of birth updated successfully.
            </Message>
          ) : null}
          <Button
            mt={3}
            width={150}
            loading={isPending}
            disabled={!newDob}
            onClick={() => {
              if (!newDob) {
                window.alert('Please select a date')
                return
              }
              const confirmed = window.confirm(
                `Are you sure you want to update User ${id}'s date of birth to ${newDob}?`
              )
              if (confirmed) {
                submitDob(newDob)
              }
            }}
          >
            Update DOB
          </Button>
        </Box>
      </Flex>
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        {...({ width: 'auto', maxWidth: '90vw' } as any)}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt="Persona Government ID - Full Size"
            onClick={() => setIsModalOpen(false)}
            style={{
              maxWidth: '85vw',
              maxHeight: '85vh',
              objectFit: 'contain',
              cursor: 'pointer',
            }}
          />
        ) : null}
      </Modal>
    </>
  )
}
